import { Facebook, Instagram, Youtube, ArrowUpRight, AtSign } from "lucide-react";
import { SOCIALS, LINKS } from "@/data/content";
import { scrollToId } from "@/components/Navbar";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .58.05.85.13V9.4a6.33 6.33 0 0 0-1-.05A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SOCIAL_ITEMS = [
  { label: "Instagram", href: SOCIALS.instagram, Icon: Instagram },
  { label: "Facebook", href: SOCIALS.facebook, Icon: Facebook },
  { label: "TikTok", href: SOCIALS.tiktok, Icon: TikTokIcon },
  { label: "YouTube", href: SOCIALS.youtube, Icon: Youtube },
  { label: "Threads", href: "https://www.threads.com/@susangrauofficial", Icon: AtSign },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="border-t border-[#F7F1E6]/10 bg-[#F7F1E6] text-[#6B5D4F]">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <button data-testid="footer-logo" onClick={() => window.__lenis?.scrollTo(0, { duration: 1.6 })} className="flex items-baseline gap-3">
              <span className="font-display text-5xl font-light">SG</span>
              <span className="font-body text-[0.6rem] font-medium uppercase tracking-[0.45em] text-[#6B5D4F]/60">Susan Grau</span>
            </button>
            <p className="mt-6 max-w-sm font-body text-sm font-light leading-relaxed text-[#6B5D4F]/55">
              Healer. International bestselling author. Inspirational speaker. Transforming life's deepest
              heartbreak into the greatest source of strength.
            </p>
            <div className="mt-8 flex gap-4" data-testid="footer-socials">
              {SOCIAL_ITEMS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-11 w-11 items-center justify-center border border-[#6B5D4F]/20 text-[#6B5D4F]/70 transition-colors duration-500 hover:border-[#C5A059] hover:text-[#C5A059]"
                >
                  <Icon size={17} strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="font-body text-[0.6rem] font-medium uppercase tracking-[0.4em] text-[#C5A059]">Explore</p>
            <nav className="mt-6 flex flex-col gap-3">
              {[
                ["Story", "#story"],
                ["Speaking", "#speaking"],
                ["Media", "#media"],
                ["Books", "#books"],
                ["Podcast", "#podcast"],
                ["The App", "#app"],
                ["Book Susan", "#press"],
              ].map(([label, href]) => (
                <button
                  key={href}
                  data-testid={`footer-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => scrollToId(href)}
                  className="gold-underline w-fit pb-0.5 text-left font-body text-sm font-light text-[#6B5D4F]/70 transition-colors duration-300 hover:text-[#6B5D4F]"
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>

          <div className="lg:col-span-4">
            <p className="font-body text-[0.6rem] font-medium uppercase tracking-[0.4em] text-[#C5A059]">Elsewhere</p>
            <div className="mt-6 flex flex-col gap-3">
              <a data-testid="footer-link-main-site" href={LINKS.mainSite} target="_blank" rel="noopener noreferrer" className="group flex w-fit items-center gap-2 font-body text-sm font-light text-[#6B5D4F]/70 transition-colors duration-300 hover:text-[#C5A059]">
                susangrau.com <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a data-testid="footer-link-press-kit" href={LINKS.pressSite} target="_blank" rel="noopener noreferrer" className="group flex w-fit items-center gap-2 font-body text-sm font-light text-[#6B5D4F]/70 transition-colors duration-300 hover:text-[#C5A059]">
                Press kit — susangraupress.com <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a data-testid="footer-link-app" href={LINKS.app} target="_blank" rel="noopener noreferrer" className="group flex w-fit items-center gap-2 font-body text-sm font-light text-[#6B5D4F]/70 transition-colors duration-300 hover:text-[#C5A059]">
                myinfinite.app <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </div>
            <p className="mt-10 font-script text-2xl text-[#C5A059]/70">hope can always be found</p>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-[#6B5D4F]/10 pt-8 md:flex-row md:items-center">
          <p className="font-body text-[0.65rem] font-light tracking-[0.2em] text-[#6B5D4F]/40">
            © {new Date().getFullYear()} Susan Grau. All rights reserved.
          </p>
          <p className="font-body text-[0.65rem] font-light tracking-[0.2em] text-[#6B5D4F]/40">
            Contact: susan@susangrau.com · Media: Meghan Amaral — M Media Corp — meghan@mmediacorp.com — <span className="whitespace-nowrap">+1 (949) 735-3732</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
